import { KeyRound, LogOut } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';

interface TopBarProps {
  userEmail?: string | null;
  // AOZONE-100: when true, the user menu shows an "MCP tokens" link to the
  // dedicated /user/tokens page. AOZONE-102: set by every shell that has
  // resolved an identity (admin dashboard, contributor TeamView, tokens page);
  // the shells that have not — the tokens route's loading state and
  // IdentityLinksView — omit it, which is why it stays an explicit prop.
  showTokensLink?: boolean;
}

function initialsFromEmail(email: string | null | undefined): string {
  if (!email) return '?';
  const local = email.split('@')[0] ?? '';
  const first = local[0];
  return first ? first.toUpperCase() : '?';
}

async function signOut(): Promise<void> {
  // Note: /api/auth/logout doesn't exist server-side today (see AOZONE-37
  // notes). We POST best-effort so when the endpoint lands the flow already
  // works; either way we then redirect to /api/auth/login which forces a
  // fresh OAuth round-trip.
  try {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
  } catch {
    /* ignore */
  }
  window.location.assign('/api/auth/login');
}

/**
 * Sticky top bar. AOZONE-37: OZONE wordmark on the left, user identity +
 * Sign Out dropdown on the right. AOZONE-108: the wordmark links to `/`.
 */
export function TopBar({ userEmail, showTokensLink }: TopBarProps) {
  const initials = initialsFromEmail(userEmail);
  return (
    <header className="sticky top-0 z-40 w-full border-b border-slate-200 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80">
      <div className="mx-auto flex h-14 max-w-[1280px] items-center justify-between px-6">
        {/* AOZONE-108: the wordmark links home. A real <a href="/"> rather
            than the window.location.assign used elsewhere in this file —
            navigation is a full page load either way, and the anchor gets
            middle-click, cmd-click, focus and link semantics for free. The
            anchor carries the flex/baseline layout the wrapping <div> owned,
            so the rendered box is unchanged; Tailwind preflight already
            resets anchor color and text-decoration, so no reset is needed. */}
        <a
          href="/"
          className="flex items-baseline gap-2 rounded-sm transition-opacity hover:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <span className="text-base font-semibold tracking-tight text-slate-900">
            OZONE
          </span>
          <span className="text-xs uppercase tracking-wider text-slate-400">
            MBR
          </span>
        </a>
        <DropdownMenu>
          <DropdownMenuTrigger
            aria-label="Account menu"
            className="flex items-center gap-2 rounded-md px-2 py-1 text-sm text-slate-700 transition-colors hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <Avatar className="h-8 w-8">
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
            {userEmail ? (
              <span className="hidden sm:inline">{userEmail}</span>
            ) : null}
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[200px]">
            <DropdownMenuLabel className="text-xs font-medium text-slate-500">
              {userEmail ?? 'Signed in'}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {showTokensLink && (
              <DropdownMenuItem
                onSelect={() => window.location.assign('/user/tokens')}
              >
                <KeyRound className="mr-2 h-4 w-4" />
                MCP tokens
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onSelect={() => void signOut()}>
              <LogOut className="mr-2 h-4 w-4" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
